// EventDetailTab — the default tab showing event fields with inline editing.
// Simpler than controls — events have: title, description, event type, event date.
// Plus an Activate button for draft → active transition.

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  eventsApi,
  eventTypesApi,
  eventLifecycleStateLabels,
  eventLifecycleStateColors,
  type UpdateEventRequest,
} from '@/lib/api/events'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Pencil, Check, X, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export function EventDetailTab() {
  const { slug } = useTenant()
  const { eventId } = useParams({ strict: false }) as { eventId: string }
  const api = useApi()
  const evtApi = eventsApi(api)
  const typeApi = eventTypesApi(api)
  const queryClient = useQueryClient()

  const { data: event, isLoading } = useQuery({
    queryKey: queryKeys.events.detail(slug, eventId),
    queryFn: () => evtApi.get(eventId),
  })

  const { data: eventTypes } = useQuery({
    queryKey: queryKeys.events.types(slug),
    queryFn: () => typeApi.list(),
  })

  // --- Editing state ---
  const [editing, setEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftEventTypeId, setDraftEventTypeId] = useState('')
  const [draftEventDate, setDraftEventDate] = useState('')

  // Sync draft state from server data
  useEffect(() => {
    if (event) {
      setDraftTitle(event.title)
      setDraftDescription(event.description ?? '')
      setDraftEventTypeId(event.event_type_id)
      setDraftEventDate(event.event_date ?? '')
    }
  }, [event])

  const isDirty = useMemo(() => {
    if (!event) return false
    return (
      draftTitle !== event.title ||
      (draftDescription || '') !== (event.description ?? '') ||
      draftEventTypeId !== event.event_type_id ||
      (draftEventDate || '') !== (event.event_date ?? '')
    )
  }, [event, draftTitle, draftDescription, draftEventTypeId, draftEventDate])

  const handleCancel = useCallback(() => {
    if (event) {
      setDraftTitle(event.title)
      setDraftDescription(event.description ?? '')
      setDraftEventTypeId(event.event_type_id)
      setDraftEventDate(event.event_date ?? '')
    }
    setEditing(false)
  }, [event])

  // --- Update mutation ---
  const updateMutation = useMutation({
    mutationFn: (req: UpdateEventRequest) => evtApi.update(eventId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(slug) })
      setEditing(false)
    },
  })

  // --- Activate mutation ---
  const activateMutation = useMutation({
    mutationFn: () => evtApi.activate(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(slug, eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all(slug) })
    },
  })

  const isMutating = updateMutation.isPending || activateMutation.isPending

  const handleApply = useCallback(() => {
    if (!event) return
    const req: UpdateEventRequest = {}
    if (draftTitle !== event.title) req.title = draftTitle
    if ((draftDescription || '') !== (event.description ?? ''))
      req.description = draftDescription || undefined
    if (draftEventTypeId !== event.event_type_id) req.event_type_id = draftEventTypeId
    if ((draftEventDate || '') !== (event.event_date ?? ''))
      req.event_date = draftEventDate || undefined
    updateMutation.mutate(req)
  }, [event, draftTitle, draftDescription, draftEventTypeId, draftEventDate, updateMutation, eventId])

  if (isLoading || !event) return <FormSkeleton />

  return (
    <div className="space-y-6 p-6">
      {/* Row 1: Core fields */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Event Details</CardTitle>
          {!editing ? (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              {isDirty && <span className="text-xs text-amber-600">Unsaved</span>}
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isMutating}>
                <X className="mr-1 h-3.5 w-3.5" /> Cancel
              </Button>
              <Button size="sm" onClick={handleApply} disabled={!isDirty || isMutating}>
                <Check className="mr-1 h-3.5 w-3.5" /> Apply Changes
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {updateMutation.isError && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {(updateMutation.error as Error).message || 'Failed to save'}
            </div>
          )}

          <EditableField label="Title" value={draftTitle} onChange={setDraftTitle} editing={editing} />
          <EditableField
            label="Description"
            value={draftDescription}
            onChange={setDraftDescription}
            editing={editing}
            multiline
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Event type dropdown */}
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Event Type
              </dt>
              {editing ? (
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={draftEventTypeId}
                  onChange={(e) => setDraftEventTypeId(e.target.value)}
                >
                  {eventTypes?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.type_name}
                    </option>
                  ))}
                </select>
              ) : (
                <dd className="text-sm">{event.event_type_name}</dd>
              )}
            </div>

            {/* Event date */}
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Event Date
              </dt>
              {editing ? (
                <Input
                  type="date"
                  value={draftEventDate}
                  onChange={(e) => setDraftEventDate(e.target.value)}
                />
              ) : (
                <dd className="text-sm">
                  {event.event_date
                    ? new Date(event.event_date + 'T00:00:00').toLocaleDateString()
                    : <span className="italic text-muted-foreground">Not set</span>}
                </dd>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Row 2: Classification & Lifecycle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Classification & Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <ReadField label="Category" value={event.category_name} />
            <ReadField label="Recorded By" value={event.recorded_by_name} />
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Lifecycle State
              </dt>
              <dd className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={cn('text-xs', eventLifecycleStateColors[event.lifecycle_state])}
                >
                  {eventLifecycleStateLabels[event.lifecycle_state]}
                </Badge>
                {event.lifecycle_state === 'draft' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={isMutating}
                    onClick={() => activateMutation.mutate()}
                  >
                    <ArrowRight className="mr-1 h-3 w-3" />
                    Activate
                  </Button>
                )}
              </dd>
            </div>
            <ReadField label="Created Via" value={event.created_via} />
          </div>
        </CardContent>
      </Card>

      {/* Row 3: Summary counts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <SummaryItem label="Linked Risks" value={event.linked_risk_count} />
            <SummaryItem label="Linked Actions" value={event.linked_action_count} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Shared helpers ---

function EditableField({
  label,
  value,
  onChange,
  editing,
  multiline,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  editing: boolean
  multiline?: boolean
}) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      {editing ? (
        multiline ? (
          <textarea
            className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input value={value} onChange={(e) => onChange(e.target.value)} />
        )
      ) : (
        <dd className="text-sm">
          {value || <span className="italic text-muted-foreground">Not set</span>}
        </dd>
      )}
    </div>
  )
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">
        {value || <span className="italic text-muted-foreground">Not set</span>}
      </dd>
    </div>
  )
}

function SummaryItem({
  label,
  value,
}: {
  label: string
  value: number | string
}) {
  return (
    <div className="rounded bg-muted/30 p-3 text-center">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
